import { type ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { customerApi } from './customer_api';

export interface CustomerAuth {
  id: string;
  email: string;
  name: string | null;
  accountSlug: string;
  /** Migration 0102 — household resolution (null until the login has a customers row). */
  household?: {
    customerId: string;
    role: 'primary' | 'member';
    memberId: string | null;
    permissions: {
      canBook: boolean;
      canPay: boolean;
      canEditPets: boolean;
      receivesNotifications: boolean;
    };
  } | null;
}

interface CustomerAuthState {
  loading: boolean;
  user: CustomerAuth | null;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const Ctx = createContext<CustomerAuthState | null>(null);

export function CustomerAuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<CustomerAuth | null>(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const me = await customerApi<CustomerAuth>('/api/customer/account/me');
      setUser(me);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    await fetch('/api/customer-auth/sign-out', { method: 'POST', credentials: 'include' });
    setUser(null);
    window.location.href = '/sign-in';
  };

  useEffect(() => {
    refresh();
  }, []);

  return <Ctx.Provider value={{ loading, user, refresh, signOut }}>{children}</Ctx.Provider>;
}

export function useCustomerAuth(): CustomerAuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useCustomerAuth must be inside CustomerAuthProvider');
  return v;
}
